import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Trophy, Eye, EyeOff, RefreshCcw, Loader2, Search, X, Edit3, MessageCircle, Sparkles, UserCheck, ShieldAlert, RotateCcw, AlertTriangle, Calculator, Settings } from 'lucide-react';
import { motion } from 'framer-motion';
import { usePolling } from '../context/PollingContext';
import { useJudgeProfiles } from '../hooks/useJudgeProfiles';
import { useRegistrationRules } from '../contexts/RegistrationRulesContext';
import StyleFeedbackEditorModal from './StyleFeedbackEditorModal';
import { CRITERIA_API, AWARDS_API, fetchWithAuth } from '../utils/api';

// Types for awards data
interface Award {
  name: string;
  color: string;
  minScore: number;
  maxScore: number;
}

interface AwardedRoutine {
  routineId: string; // This will be the routineNumber for display
  internalId: string; // This will be the actual internal ID used for scoring
  routineName: string;
  performer: string;
  studio: string;
  division: string;
  category: string;
  ageGroup: string;
  finalScore: number;
  award: string;
  rank: number;
  type: string;
  flexibleScoreData?: FlexibleScoreData;
  judgeScores?: JudgeScore[];
  stageInfo?: string;
}

interface FlexibleScoreData {
  finalScore: number;
  correlatedAward: string;
  droppedScores: any[];
  judgeScores: JudgeScore[];
  appliedRules: any[];
}

interface JudgeScore {
  judgeId: string;
  judgeName?: string;
  totalScore: number;
  scores: { [criterion: string]: number };
  isSubmitted: boolean;
  iChoreographedThis?: boolean;
  notFamilyFriendly?: boolean;
  judgePosition?: number;
  comments?: string;
  styleFeedback?: any;
}

interface ScoreData {
  scores: { [criterion: string]: number };
  isSubmitted: boolean;
  iChoreographedThis?: boolean;
  notFamilyFriendly?: boolean;
  comments?: string;
  styleFeedback?: any;
  submittedTime?: string;
  judgeData?: {
    judgeId: string;
    judgeName: string;
    judgePosition: number;
  };
}

interface AwardsTrackerProps {
  className?: string;
  enableEditing?: boolean; // New prop to enable/disable editing for tabulators
}

const AwardsTracker: React.FC<AwardsTrackerProps> = ({ className = '', enableEditing = false }) => {
  const [routines, setRoutines] = useState<AwardedRoutine[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showScores, setShowScores] = useState<boolean>(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Editing state management (similar to LiveScoreMonitor)
  const [editingScore, setEditingScore] = useState<{ routineId: string; judgeId: string; criterionId: string } | null>(null);
  const [editingComment, setEditingComment] = useState<{ routineId: string; judgeId: string } | null>(null);
  const [tempScoreValue, setTempScoreValue] = useState<string>("");
  const [tempCommentValue, setTempCommentValue] = useState<string>("");
  
  // Loading states for inline editing
  const [updatingScores, setUpdatingScores] = useState<Set<string>>(new Set());
  const [updatingComments, setUpdatingComments] = useState<Set<string>>(new Set());
  const [updatingStyleFeedback, setUpdatingStyleFeedback] = useState<Set<string>>(new Set());
  const [updatingBooleanFlags, setUpdatingBooleanFlags] = useState<Set<string>>(new Set());
  
  // Reset confirmation state - tracks which judge/routine is in reset confirmation mode
  const [resetConfirmation, setResetConfirmation] = useState<Set<string>>(new Set());
  
  // Modal states
  const [isStyleFeedbackModalOpen, setIsStyleFeedbackModalOpen] = useState(false);
  const [styleModalData, setStyleModalData] = useState<{ routineId: string; judgeId: string; routine: AwardedRoutine } | null>(null);
  
  // Criteria and scoring mode state
  const [criteria, setCriteria] = useState<any[]>([]);
  const [scoringMode, setScoringMode] = useState<'criteria' | 'simple'>('simple');
  const [criteriaLoading, setCriteriaLoading] = useState<boolean>(true);
  
  // Awards configuration state
  const [awards, setAwards] = useState<Award[]>([]);
  
  // Track the target state for boolean flags during updates
  const [booleanFlagTargets, setBooleanFlagTargets] = useState<Map<string, boolean>>(new Map());
  
  const { 
    connected, 
    competitionCode,
    scheduleData,
    judgeProfiles,
    judgePositions,
    scoreUpdates,
    updateScore,
    deleteScore,
    refreshGlobalScoreUpdates
  } = usePolling();

  const { state: registrationRulesState } = useRegistrationRules();
  const styleScoreSheets = registrationRulesState.styleScoreSheets || [];


  // Use judge profiles hook to get proper judge names
  const { judges: allJudgeProfiles, loading: judgeProfilesLoading } = useJudgeProfiles();
  
  // Custom breathing animation styles
  const breathingStyles = `
    @keyframes breathing {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.6; transform: scale(1.05); }
    }
    .animate-breathing {
      animation: breathing 2s ease-in-out infinite;
    }
    @keyframes scoreUpdating {
      0%, 100% { opacity: 1; background-color: rgba(251, 191, 36, 0.2); }
      50% { opacity: 0.7; background-color: rgba(251, 191, 36, 0.4); }
    }
    .animate-score-updating {
      animation: scoreUpdating 1.5s ease-in-out infinite;
    }
    /* Hide number input arrows/spinners */
    input[type="number"]::-webkit-outer-spin-button,
    input[type="number"]::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    input[type="number"] {
      -moz-appearance: textfield;
    }
  `;

  // Extract base competition code (remove stage and section suffixes for global access)
  const baseCompetitionCode = useMemo(() => {
    console.log('🎯 AWARDS TRACKER DEBUG - Original competitionCode:', competitionCode);
    if (!competitionCode) return null;
    // Remove _001_set-1, _001_cluster-6, _002_set-2, etc. patterns to get base competition code
    const base = competitionCode
      .replace(/_\d{3}_set-\d+$/, '')      // Remove _001_set-1 pattern (old)
      .replace(/_\d{3}_cluster-\d+$/, ''); // Remove _001_cluster-6 pattern (new)
    console.log('🎯 AWARDS TRACKER DEBUG - Base competition code:', base);
    return base;
  }, [competitionCode]);

  // State for global awards data
  const [globalScheduleData, setGlobalScheduleData] = useState<any[]>([]);
  const [globalScoreUpdates, setGlobalScoreUpdates] = useState<any>({});
  
  // Load awards configuration (similar to PerformanceSchedule)
  useEffect(() => {
    let isMounted = true;
    
    const loadAwards = async () => {
      try {
        const data = await fetchWithAuth(AWARDS_API);
        if (isMounted) {
          setAwards(data.awards || []);
        }
      } catch (err) {
        if (isMounted) {
          console.error("Error loading awards:", err);
        }
      }
    };
    
    loadAwards();
    return () => { isMounted = false; };
  }, []);
  
  // Memoized award calculation
  const getAwardForScore = useCallback((score: number): Award | null => {
    if (score === null || score === undefined || !awards?.length) return null;
    return awards.find(award => score >= award.minScore && score <= award.maxScore) || awards[awards.length - 1];
  }, [awards]);
  
  // Custom update score function that uses the routine's original competition code
  const updateScoreWithOriginalLocation = useCallback(async (scoreData: any) => {
    try {
      // Find the routine in the global schedule data to get its original competition info
      const routine = globalScheduleData.find(r => 
        r.id === scoreData.routineId || 
        r.routineNumber === scoreData.routineId ||
        String(r.id) === String(scoreData.routineId) ||
        String(r.routineNumber) === String(scoreData.routineId)
      );
      
      if (routine && routine.competitionId) {
        // Use the routine's original competition code for the API call
        const originalCompetitionCode = routine.competitionId;
        
        // Make direct API call with the original competition code
        const AWS_API_BASE_URL = process.env.REACT_APP_API_URL || 'https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev';
        const endpoint = `/competition/${originalCompetitionCode}/score/update`;
        
        // Get authentication token
        let token = null;
        try {
          const { fetchAuthSession } = await import('@aws-amplify/auth');
          const session = await fetchAuthSession();
          token = session.tokens?.idToken?.toString();
        } catch (error) {
          console.warn('Could not get auth token:', error);
        }
        
        const headers: Record<string, string> = {
          'Content-Type': 'application/json'
        };
        
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(`${AWS_API_BASE_URL}${endpoint}`, {
          method: 'POST',
          headers,
          body: JSON.stringify(scoreData)
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        return true;
      } else {
        // Fallback to the regular updateScore if we can't find the routine's original location
        console.log(`⚠️ AWARDS TRACKER - Could not find original location for routine ${scoreData.routineId}, using fallback`);
        return await updateScore(scoreData);
      }
    } catch (error) {
      console.error('Failed to update score with original location:', error);
      throw error;
    }
  }, [globalScheduleData, competitionCode, updateScore]);
  
  // Create a comprehensive judge profiles map from both polling context and judge profiles hook
  const comprehensiveJudgeProfiles = useMemo(() => {
    const profiles: any = {};
    
    // Add profiles from polling context (these are session-specific)
    Object.entries(judgeProfiles || {}).forEach(([judgeId, profile]) => {
      profiles[judgeId] = profile;
    });
    
    // Add profiles from judge profiles hook (these are global judge data)
    allJudgeProfiles.forEach((judge: any) => {
      if (judge.id) {
        profiles[judge.id] = {
          id: judge.id,
          name: judge.name,
          title: judge.title,
          photoUrl: judge.photoUrl
        };
      }
    });
    
    return profiles;
  }, [judgeProfiles, allJudgeProfiles]);

  // Helper function to get studio name
  const getStudioName = (scheduleRoutine: any): string => {
    if (scheduleRoutine.studioName) return scheduleRoutine.studioName;
    
    try {
      const enhancedStudioName = scheduleRoutine.parentRoutine?.submissionRoutines?.[0]?.registrations?.studios?.businessName;
      if (enhancedStudioName) return enhancedStudioName;
    } catch (error) {
      // Continue to fallback
    }
    
    return scheduleRoutine.studioCode || 'Unknown Studio';
  };

  // Fetch global awards data from base competition
  const fetchGlobalAwardsData = useCallback(async () => {
    console.log('🎯 AWARDS TRACKER DEBUG - fetchGlobalAwardsData called');
    console.log('🎯 AWARDS TRACKER DEBUG - baseCompetitionCode:', baseCompetitionCode);
    
    if (!baseCompetitionCode) {
      console.log('❌ AWARDS TRACKER DEBUG - No base competition code, returning early');
      return;
    }
    
    setIsLoading(true);
    try {
      const AWS_API_BASE_URL = process.env.REACT_APP_API_URL || 'https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev';
      const endpoint = `/competition/${baseCompetitionCode}/scores/all`;
      
      console.log('🎯 AWARDS TRACKER DEBUG - Fetching from:', `${AWS_API_BASE_URL}${endpoint}`);
      
      const response = await fetch(`${AWS_API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('🎯 AWARDS TRACKER DEBUG - Response status:', response.status, response.ok);
      
      if (!response.ok) {
        console.error(`❌ AWARDS TRACKER DEBUG - Failed to fetch global awards: ${response.status}`);
        return;
      }
      
      const data = await response.json();
      console.log('🎯 AWARDS TRACKER DEBUG - Raw API response:', data);
      console.log('🎯 AWARDS TRACKER DEBUG - scheduleData length:', (data.scheduleData || []).length);
      console.log('🎯 AWARDS TRACKER DEBUG - scoreUpdates keys:', Object.keys(data.scoreUpdates || {}).length);
      
      // Apply deduplication to the schedule data (same logic as AwardsVisualizerPage)
      const deduplicateScheduleData = (scheduleData: any[]): any[] => {
        const routineMap = new Map<string, any>();
        const duplicatesFound = new Set<string>();
        
        scheduleData.forEach(routine => {
          const routineNumber = routine.routineNumber || routine.id;
          if (!routineNumber) return;
          
          const existing = routineMap.get(routineNumber);
          if (!existing) {
            routineMap.set(routineNumber, routine);
          } else {
            duplicatesFound.add(routineNumber);
            
            // Keep the routine with better score data or more complete information
            const existingScore = getFlexibleScoreDataFromUpdates(existing.id || existing.routineNumber, data.scoreUpdates || {});
            const currentScore = getFlexibleScoreDataFromUpdates(routine.id || routine.routineNumber, data.scoreUpdates || {});
            
            // Prefer the routine with score data, or the one with higher score
            if (!existingScore && currentScore) {
              routineMap.set(routineNumber, routine);
            } else if (existingScore && currentScore && currentScore.finalScore > existingScore.finalScore) {
              routineMap.set(routineNumber, routine);
            }
            // Otherwise keep the existing routine (implicit - no action needed)
          }
        });
        
        return Array.from(routineMap.values());
      };
      
      // Apply deduplication
      const deduplicatedScheduleData = deduplicateScheduleData(data.scheduleData || []);
      
      console.log('🎯 AWARDS TRACKER DEBUG - After deduplication:', deduplicatedScheduleData.length);
      
      // Set global data
      setGlobalScheduleData(deduplicatedScheduleData);
      setGlobalScoreUpdates(data.scoreUpdates || {});
      
      console.log(`✅ AWARDS TRACKER DEBUG - Fetched global awards data for ${baseCompetitionCode}:`, {
        scheduleCount: deduplicatedScheduleData.length,
        scoreUpdatesCount: Object.keys(data.scoreUpdates || {}).length
      });
      
    } catch (error) {
      console.error('Error fetching global awards data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [baseCompetitionCode]);

  // Fetch global data when base competition code changes
  useEffect(() => {
    fetchGlobalAwardsData();
  }, [fetchGlobalAwardsData]);
  
  // Load criteria for score editing
  useEffect(() => {
    const loadCriteria = async () => {
      if (!enableEditing) return;
      
      try {
        setCriteriaLoading(true);
        const data = await fetchWithAuth(CRITERIA_API);
        
        // Set scoring mode from API response
        setScoringMode(data.scoringMode || 'simple');
        
        if (data.scoringMode === 'simple') {
          // For simple scoring, we don't need criteria
          setCriteria([]);
        } else if (data && data.criteria && data.criteria.length > 0) {
          // Process criteria to ensure they have weight and maxPoints properties
          const processedCriteria = data.criteria.map((criterion: any) => ({
            ...criterion,
            // Use existing weight or calculate from maxPoints
            weight: criterion.weight || 
                   (criterion.maxPoints && data.isPercentageMode ? 
                    (criterion.maxPoints / 
                     data.criteria!.reduce((sum: number, c: any) => sum + (c.maxPoints || 0), 0)) * 100 
                    : 0),
            // Ensure maxPoints exists
            maxPoints: criterion.maxPoints || 0,
            // Use either name or label property
            name: criterion.name || criterion.label || `Criterion ${criterion.id}`
          }));
          
          setCriteria(processedCriteria);
        } else {
          console.error("No criteria configured in judging and awards settings");
          setCriteria([]);
        }
      } catch (err) {
        console.error("Error loading criteria:", err);
        setCriteria([]);
      } finally {
        setCriteriaLoading(false);
      }
    };

    loadCriteria();
  }, [enableEditing]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl/Cmd + F to focus search
      if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        event.preventDefault();
        const searchInput = document.querySelector('input[placeholder*="Search routines"]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
      }
      
      // Escape to clear search
      if (event.key === 'Escape' && searchTerm) {
        clearSearch();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [searchTerm]);

  // Helper function to get flexible scoring data from a specific scoreUpdates object
  const getFlexibleScoreDataFromUpdates = (routineId: string, scoreUpdates: any): FlexibleScoreData | null => {
    const judgeScores: Array<{judgeId: string; totalScore: number;}> = [];
    
    // Look for individual judge scores with pattern: routineId_judgeId
    Object.keys(scoreUpdates).forEach(key => {
      if (key.startsWith(`${routineId}_`) && !key.includes('flexible')) {
        const scoreData = scoreUpdates[key];
        if (scoreData && scoreData.isSubmitted) {
          // Use simpleScore - try both top level and nested location
          const judgeScore = scoreData.simpleScore || scoreData.scores?.simpleScore || 0;
          
          judgeScores.push({
            judgeId: scoreData.judgeId || key.split('_')[1],
            totalScore: judgeScore
          });
        }
      }
    });

    // Need at least 3 scores to display
    if (judgeScores.length >= 3) {
      // Sort scores from highest to lowest
      const sortedScores = [...judgeScores].sort((a, b) => b.totalScore - a.totalScore);
      
      let finalScore = 0;
      let droppedScores: Array<{judgeId: string; totalScore: number; dropReason?: string}> = [];
      let appliedRules: string[] = [];
      
      if (sortedScores.length === 3) {
        // 3 judges: sum all scores, show 0 as dropped score for consistency
        finalScore = sortedScores.reduce((sum, score) => sum + score.totalScore, 0);
        droppedScores = [{
          judgeId: 'none',
          totalScore: 0,
          dropReason: 'No 4th judge'
        }];
        appliedRules.push('3 judges: all scores counted');
      } else if (sortedScores.length >= 4) {
        // 4+ judges: sum the highest 3, drop the lowest
        const topThree = sortedScores.slice(0, 3);
        const dropped = sortedScores.slice(3);
        
        finalScore = topThree.reduce((sum, score) => sum + score.totalScore, 0);
        droppedScores = dropped.map(score => ({
          judgeId: score.judgeId,
          totalScore: score.totalScore,
          dropReason: 'Lowest score dropped'
        }));
        appliedRules.push(`${sortedScores.length} judges: highest 3 scores counted, lowest ${dropped.length} dropped`);
      }
      
      // Get correlated award based on final score
      const award = getAwardForScore(finalScore);
      const correlatedAward = award ? award.name : 'No Award';
      
      return {
        finalScore,
        judgeScores: sortedScores.map(s => ({
          judgeId: s.judgeId,
          totalScore: s.totalScore,
          scores: {}, // Not available in simple format
          isSubmitted: true
        })) as any,
        droppedScores,
        appliedRules,
        correlatedAward
      };
    }

    return null;
  };

  // Helper function to get flexible scoring data from global data
  const getFlexibleScoreData = (routineId: string): FlexibleScoreData | null => {
    return getFlexibleScoreDataFromUpdates(routineId, globalScoreUpdates);
  };
  
  // Helper function to format scores without unnecessary decimals
  const formatScore = (score: number): string => {
    // If the score is a whole number, return it without decimals
    if (score % 1 === 0) {
      return score.toString();
    }
    // Otherwise, return with one decimal place
    return score.toFixed(1);
  };
  
  // Helper function to get the display state for boolean flags (current or target during loading)
  const getBooleanFlagDisplayState = (routineId: string, judgeId: string, flagType: 'iChoreographedThis' | 'notFamilyFriendly', currentValue: boolean): boolean => {
    const updateKey = `${routineId}_${judgeId}_${flagType}`;
    if (updatingBooleanFlags.has(updateKey)) {
      // During loading, show the target state
      return booleanFlagTargets.get(updateKey) ?? currentValue;
    }
    return currentValue;
  };
  
  // Helper function to get score data for a specific routine and judge
  const getScoreDataForRoutineJudge = (routineId: string, judgeId: string) => {
    const scoreKey = `${routineId}_${judgeId}`;
    return globalScoreUpdates[scoreKey];
  };

  // Helper function to get individual judge scores for a routine from global data
  const getJudgeScores = (routineId: string): JudgeScore[] => {
    const judgeScores: JudgeScore[] = [];
    
    // Look through global scoreUpdates for this routine
    Object.entries(globalScoreUpdates).forEach(([key, scoreData]) => {
      if (key.startsWith(`${routineId}_`) && !key.endsWith('_flexible')) {
        const judgeId = key.split('_').slice(1).join('_');
        
        // Type guard to ensure scoreData has the expected structure
        const typedScoreData = scoreData as ScoreData;
        
        if (typedScoreData && typedScoreData.scores) {
          // Calculate total score from individual criterion scores
          const totalScore = Object.values(typedScoreData.scores)
            .reduce((sum, score) => sum + (score || 0), 0);
          
          const judgeProfile = comprehensiveJudgeProfiles[judgeId];
          
          // Extract judge position from judgeData if available, fallback to judgePositions map
          let judgePosition = typedScoreData.judgeData?.judgePosition;
          
          // Fallback: Use judgePositions from PollingContext if score data doesn't have it
          if (judgePosition === undefined || judgePosition === null) {
            judgePosition = judgePositions?.[judgeId];
          }
          
          judgeScores.push({
            judgeId,
            judgeName: judgeProfile?.name || judgeId,
            totalScore,
            scores: typedScoreData.scores,
            isSubmitted: typedScoreData.isSubmitted || false,
            iChoreographedThis: typedScoreData.iChoreographedThis || false,
            notFamilyFriendly: typedScoreData.notFamilyFriendly || false,
            judgePosition: judgePosition,
            comments: typedScoreData.comments,
            styleFeedback: typedScoreData.styleFeedback
          });
        }
      }
    });
    
    // Sort by judge position (ascending), with judges without positions at the end
    return judgeScores.sort((a, b) => {
      // If both have positions, sort by position (ascending)
      if (a.judgePosition !== undefined && b.judgePosition !== undefined) {
        return a.judgePosition - b.judgePosition;
      }
      
      // If only one has a position, prioritize the one with position
      if (a.judgePosition !== undefined) return -1;
      if (b.judgePosition !== undefined) return 1;
      
      // If neither has a position, sort by total score (descending) as fallback
      return b.totalScore - a.totalScore;
    });
  };

  // Process global awards data
  const processGlobalAwardsData = useMemo(() => {
    console.log('🎯 AWARDS TRACKER DEBUG - processGlobalAwardsData called');
    console.log('🎯 AWARDS TRACKER DEBUG - globalScheduleData length:', globalScheduleData.length);
    console.log('🎯 AWARDS TRACKER DEBUG - globalScoreUpdates keys:', Object.keys(globalScoreUpdates).length);
    
    if (!globalScheduleData || globalScheduleData.length === 0) {
      console.log('❌ AWARDS TRACKER DEBUG - No schedule data, setting empty routines');
      setRoutines([]);
      return;
    }
    
    try {
      const awardedRoutines: AwardedRoutine[] = [];
      
      // Process each routine from global schedule data
      globalScheduleData.forEach((scheduleRoutine: any) => {
        const routineId = scheduleRoutine.id || scheduleRoutine.routineNumber;
        const flexibleScoreData = getFlexibleScoreData(routineId);
        
        // Only include routines that have been scored and have awards
        if (flexibleScoreData && flexibleScoreData.correlatedAward && flexibleScoreData.correlatedAward !== 'No Award') {
          const isSolo = scheduleRoutine.division === 'Solo';
          const routineTitle = scheduleRoutine.name || `Routine #${scheduleRoutine.routineNumber}`;
          const performerName = scheduleRoutine.performerName || '';
          
          // For solos, combine title and performer name (only if not already included)
          const displayTitle = isSolo && performerName && !routineTitle.includes(performerName)
            ? `${routineTitle} (${performerName})`
            : routineTitle;
          
          // Get individual judge scores
          const judgeScores = getJudgeScores(routineId);
          
          // Add stage information for global view
          const stageInfo = scheduleRoutine.stageName || 
                           (scheduleRoutine.stageId ? `Stage ${parseInt(scheduleRoutine.stageId)}` : '');
          
          awardedRoutines.push({
            routineId: scheduleRoutine.routineNumber, // Display ID (e.g., "326")
            internalId: scheduleRoutine.id || scheduleRoutine.routineNumber, // Internal ID for scoring (e.g., "1421")
            routineName: displayTitle,
            performer: performerName,
            studio: getStudioName(scheduleRoutine),
            division: scheduleRoutine.division || 'Unknown',
            category: scheduleRoutine.category || 'Competitive',
            ageGroup: scheduleRoutine.ageGroup || 'Unknown',
            finalScore: flexibleScoreData.finalScore,
            award: flexibleScoreData.correlatedAward,
            rank: 0, // Will be set after sorting
            type: scheduleRoutine.type || 'Competitive',
            flexibleScoreData: flexibleScoreData,
            judgeScores: judgeScores,
            stageInfo: stageInfo // Add stage info for global view
          });
        }
      });
      
      // Sort by routine number (ascending order)
      awardedRoutines.sort((a, b) => parseInt(a.routineId) - parseInt(b.routineId));
      
      // Assign ranks based on sorted order
      awardedRoutines.forEach((routine, index) => {
        routine.rank = index + 1;
      });
      
      console.log(`✅ AWARDS TRACKER DEBUG - Processed ${awardedRoutines.length} awarded routines`);
      console.log('🎯 AWARDS TRACKER DEBUG - Sample awarded routines:', awardedRoutines.slice(0, 3));
      
      setRoutines(awardedRoutines);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error processing global awards data:', error);
      setRoutines([]);
    }
  }, [globalScheduleData, globalScoreUpdates, comprehensiveJudgeProfiles, judgePositions]);

  // Automatic score updates from polling context (similar to LiveScoreMonitor)
  useEffect(() => {
    if (!connected || !scoreUpdates) return;

    // Merge polling scoreUpdates with existing globalScoreUpdates
    // This allows us to get real-time updates for the current stage while preserving global data
    setGlobalScoreUpdates((prevGlobalScores: any) => {
      const mergedScores = { ...prevGlobalScores };
      
      // Deep merge: preserve judgeData if incoming data lacks position info
      Object.entries(scoreUpdates).forEach(([key, newScoreData]: [string, any]) => {
        const existingScoreData = prevGlobalScores[key];
        
        if (existingScoreData?.judgeData?.judgePosition !== undefined && existingScoreData?.judgeData?.judgePosition !== null) {
          // Preserve existing judgeData if new data doesn't have position
          if (!newScoreData.judgeData?.judgePosition) {
            mergedScores[key] = {
              ...newScoreData,
              judgeData: existingScoreData.judgeData // Preserve the existing judgeData
            };
          } else {
            // New data has position info, use it
            mergedScores[key] = newScoreData;
          }
        } else {
          // No existing position data, use new data as-is
          mergedScores[key] = newScoreData;
        }
      });
      
      // Automatic score updates merged - logging removed to reduce console spam
      
      return mergedScores;
    });
    
    // Update last updated timestamp to trigger UI refresh
    setLastUpdated(new Date());
  }, [connected, scoreUpdates]);

  // Automatic schedule updates from polling context
  useEffect(() => {
    if (!connected || !scheduleData) return;

    // Merge polling scheduleData with existing globalScheduleData
    // This allows us to get real-time updates for the current stage while preserving global data
    setGlobalScheduleData((prevGlobalSchedule: any[]) => {
      // Create a map of existing routines by their ID for efficient merging
      const existingRoutinesMap = new Map();
      prevGlobalSchedule.forEach(routine => {
        const routineId = routine.id || routine.routineNumber;
        if (routineId) {
          existingRoutinesMap.set(routineId, routine);
        }
      });
      
      // Update or add routines from polling data
      scheduleData.forEach(routine => {
        const routineId = routine.id || routine.routineNumber;
        if (routineId) {
          existingRoutinesMap.set(routineId, routine);
        }
      });
      
      const mergedSchedule = Array.from(existingRoutinesMap.values());
      
      // Automatic schedule updates merged - logging removed to reduce console spam
      
      return mergedSchedule;
    });
  }, [connected, scheduleData]);

  // Data is automatically updated via useMemo when dependencies change
  
  // ============================================================================
  // EDITING HANDLERS (Similar to LiveScoreMonitor)
  // ============================================================================
  
  // Handle inline score editing
  const handleScoreClick = useCallback((routineId: string, judgeId: string, criterionId: string) => {
    if (!enableEditing) return;
    
    const scoreData = getScoreDataForRoutineJudge(routineId, judgeId);
    let currentScore = 0;
    
    if (criterionId === 'totalScore') {
      // For total score, calculate from all criteria or use simple score
      if (scoringMode === 'simple') {
        currentScore = scoreData?.scores?.simpleScore || 0;
      } else {
        // Calculate total from all criteria
        currentScore = criteria.reduce((sum, criterion) => {
          return sum + (scoreData?.scores?.[criterion.id] || 0);
        }, 0);
      }
    } else {
      currentScore = scoreData?.scores?.[criterionId] || 0;
    }
    
    setEditingScore({ routineId, judgeId, criterionId });
    setTempScoreValue(currentScore.toString());
  }, [enableEditing, globalScoreUpdates, criteria, scoringMode]);

  const handleScoreBlur = useCallback(async () => {
    if (!editingScore || !updateScore) {
      setEditingScore(null);
      return;
    }

    const newScore = parseFloat(tempScoreValue);
    
    // Validation
    if (isNaN(newScore)) {
      // Invalid number, revert to original value
      setEditingScore(null);
      setTempScoreValue("");
      return;
    }

    // Get max points for validation
    let maxPoints = 100; // Default for simple scoring
    if (editingScore.criterionId === 'totalScore') {
      if (scoringMode === 'simple') {
        maxPoints = 100;
      } else {
        maxPoints = criteria.reduce((sum, c) => sum + (c.maxPoints || 10), 0);
      }
    } else if (editingScore.criterionId !== 'simpleScore') {
      const criterion = criteria.find(c => c.id === editingScore.criterionId);
      maxPoints = criterion?.maxPoints || 10;
    }

    // Clamp score to valid range
    const clampedScore = Math.max(0, Math.min(maxPoints, newScore));
    
    if (clampedScore !== newScore) {
      // Score was clamped, show warning but still save
      console.warn(`Score ${newScore} was clamped to ${clampedScore} (max: ${maxPoints})`);
    }

    // Create unique key for this score update
    const updateKey = `${editingScore.routineId}_${editingScore.judgeId}_${editingScore.criterionId}`;
    
    // Set loading state
    setUpdatingScores(prev => new Set([...prev, updateKey]));
    setEditingScore(null);
    setTempScoreValue("");

    try {
      // Get existing score data to preserve other fields
      const existingScoreData = getScoreDataForRoutineJudge(editingScore.routineId, editingScore.judgeId);
      let updatedScores = { ...existingScoreData?.scores };
      
      if (editingScore.criterionId === 'totalScore') {
        // For total score editing, we need to update the appropriate underlying score
        if (scoringMode === 'simple') {
          updatedScores.simpleScore = clampedScore;
        } else {
          // For criteria-based scoring, distribute the total score proportionally
          const currentTotal = criteria.reduce((sum, criterion) => {
            return sum + (existingScoreData?.scores?.[criterion.id] || 0);
          }, 0);
          
          if (currentTotal > 0) {
            // Distribute proportionally
            const ratio = clampedScore / currentTotal;
            criteria.forEach(criterion => {
              const currentCriterionScore = existingScoreData?.scores?.[criterion.id] || 0;
              updatedScores[criterion.id] = Math.round(currentCriterionScore * ratio * 100) / 100;
            });
          } else {
            // If current total is 0, distribute evenly
            const scorePerCriterion = clampedScore / criteria.length;
            criteria.forEach(criterion => {
              updatedScores[criterion.id] = Math.round(scorePerCriterion * 100) / 100;
            });
          }
        }
      } else {
        updatedScores[editingScore.criterionId] = clampedScore;
      }
      
      await updateScoreWithOriginalLocation({
        judgeId: editingScore.judgeId,
        routineId: editingScore.routineId,
        routineIndex: 0, // Not used for global updates
        scores: updatedScores,
        comments: existingScoreData?.comments || '',
        isSubmitted: existingScoreData?.isSubmitted || false
      });
      
      // Refresh global scores to get updated flexible scores immediately
      await refreshGlobalScoreUpdates();
      
      // Clear loading state after refresh
      setUpdatingScores(prev => {
        const newSet = new Set(prev);
        newSet.delete(updateKey);
        return newSet;
      });
      
    } catch (error) {
      console.error('Score update failed:', error);
      // Clear loading state immediately on error
      setUpdatingScores(prev => {
        const newSet = new Set(prev);
        newSet.delete(updateKey);
        return newSet;
      });
    }
  }, [editingScore, tempScoreValue, updateScoreWithOriginalLocation, criteria, globalScoreUpdates, scoringMode]);

  const handleScoreKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleScoreBlur();
    } else if (e.key === 'Escape') {
      setEditingScore(null);
      setTempScoreValue("");
    }
  }, [handleScoreBlur]);

  // Handle inline comment editing
  const handleCommentClick = useCallback((routineId: string, judgeId: string) => {
    if (!enableEditing) return;
    
    const scoreData = getScoreDataForRoutineJudge(routineId, judgeId);
    const currentComment = scoreData?.comments || '';
    setEditingComment({ routineId, judgeId });
    setTempCommentValue(currentComment);
  }, [enableEditing, globalScoreUpdates]);

  const handleCommentBlur = useCallback(async () => {
    if (!editingComment || !updateScore) {
      setEditingComment(null);
      return;
    }

    // Trim whitespace and validate length
    const trimmedComment = tempCommentValue.trim();
    const maxCommentLength = 1000; // Match backend validation
    
    if (trimmedComment.length > maxCommentLength) {
      console.warn(`Comment length ${trimmedComment.length} exceeds maximum ${maxCommentLength}`);
      // Truncate the comment
      const truncatedComment = trimmedComment.substring(0, maxCommentLength);
      
      // Set loading state
      const updateKey = `${editingComment.routineId}_${editingComment.judgeId}`;
      setUpdatingComments(prev => new Set([...prev, updateKey]));
      setEditingComment(null);
      setTempCommentValue("");

      try {
        // Get existing score data to preserve other fields
        const existingScoreData = getScoreDataForRoutineJudge(editingComment.routineId, editingComment.judgeId);
        
        await updateScoreWithOriginalLocation({
          judgeId: editingComment.judgeId,
          routineId: editingComment.routineId,
          routineIndex: 0, // Not used for global updates
          scores: existingScoreData?.scores || {},
          comments: truncatedComment,
          isSubmitted: existingScoreData?.isSubmitted || false
        });
        
        // Clear loading state after a brief delay
        setTimeout(() => {
          setUpdatingComments(prev => {
            const newSet = new Set(prev);
            newSet.delete(updateKey);
            return newSet;
          });
        }, 1500);
        
      } catch (error) {
        console.error('Comment update failed:', error);
        setUpdatingComments(prev => {
          const newSet = new Set(prev);
          newSet.delete(updateKey);
          return newSet;
        });
      }
    } else {
      // Set loading state
      const updateKey = `${editingComment.routineId}_${editingComment.judgeId}`;
      setUpdatingComments(prev => new Set([...prev, updateKey]));
      setEditingComment(null);
      setTempCommentValue("");

      try {
        // Get existing score data to preserve other fields
        const existingScoreData = getScoreDataForRoutineJudge(editingComment.routineId, editingComment.judgeId);
        
        await updateScoreWithOriginalLocation({
          judgeId: editingComment.judgeId,
          routineId: editingComment.routineId,
          routineIndex: 0, // Not used for global updates
          scores: existingScoreData?.scores || {},
          comments: trimmedComment,
          isSubmitted: existingScoreData?.isSubmitted || false
        });
        
        // Clear loading state after a brief delay
        setTimeout(() => {
          setUpdatingComments(prev => {
            const newSet = new Set(prev);
            newSet.delete(updateKey);
            return newSet;
          });
        }, 1500);
        
      } catch (error) {
        console.error('Comment update failed:', error);
        setUpdatingComments(prev => {
          const newSet = new Set(prev);
          newSet.delete(updateKey);
          return newSet;
        });
      }
    }
  }, [editingComment, tempCommentValue, updateScore, globalScoreUpdates]);

  const handleCommentKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCommentBlur();
    } else if (e.key === 'Escape') {
      setEditingComment(null);
      setTempCommentValue("");
    }
  }, [handleCommentBlur]);

  // Style feedback update handler
  const handleStyleFeedbackUpdate = useCallback(async (judgeId: string, category: string, criteria: string, feedbackType: 'good' | 'needs-work' | null, batchedStyleFeedback?: any) => {
    if (!updateScore) return;

    // Use the routineId from styleModalData
    const routineId = styleModalData?.routineId;
    if (!routineId) return;

    try {
      // Get existing score data to preserve other fields
      const existingScoreData = getScoreDataForRoutineJudge(routineId, judgeId);
      
      if (!existingScoreData) {
        console.error('No existing score data found for this judge');
        return;
      }

      let updatedStyleFeedback;
      
      if (batchedStyleFeedback) {
        // Handle batched update (from modal)
        console.log('Processing batched style feedback update:', batchedStyleFeedback);
        updatedStyleFeedback = batchedStyleFeedback;
      } else {
        // Handle individual update (legacy support)
        const updateKey = `${routineId}_${judgeId}_${category}_${criteria}`;
        setUpdatingStyleFeedback(prev => new Set([...prev, updateKey]));

        updatedStyleFeedback = { ...existingScoreData.styleFeedback };
        
        if (!updatedStyleFeedback.good) updatedStyleFeedback.good = [];
        if (!updatedStyleFeedback['needs-work']) updatedStyleFeedback['needs-work'] = [];
        
        // Remove from both arrays first
        updatedStyleFeedback.good = updatedStyleFeedback.good.filter(
          (item: any) => !(item.category === category && item.criteria === criteria)
        );
        updatedStyleFeedback['needs-work'] = updatedStyleFeedback['needs-work'].filter(
          (item: any) => !(item.category === category && item.criteria === criteria)
        );
        
        // Add to appropriate array if feedbackType is not null
        if (feedbackType) {
          updatedStyleFeedback[feedbackType].push({ category, criteria });
        }

        // Clear loading state after a brief delay for individual updates
        setTimeout(() => {
          setUpdatingStyleFeedback(prev => {
            const newSet = new Set(prev);
            newSet.delete(updateKey);
            return newSet;
          });
        }, 1500);
      }

      await updateScoreWithOriginalLocation({
        judgeId: judgeId,
        routineId: routineId,
        routineIndex: 0, // Not used for global updates
        scores: existingScoreData.scores || {},
        comments: existingScoreData.comments || '',
        isSubmitted: existingScoreData.isSubmitted || false,
        styleFeedback: updatedStyleFeedback,
        // Preserve boolean flags
        iChoreographedThis: existingScoreData.iChoreographedThis || false,
        notFamilyFriendly: existingScoreData.notFamilyFriendly || false
      });
      
      console.log('Style feedback updated successfully');
      
    } catch (error) {
      console.error('Style feedback update failed:', error);
      // Clear loading state on error for individual updates
      if (!batchedStyleFeedback) {
        const updateKey = `${routineId}_${judgeId}_${category}_${criteria}`;
        setUpdatingStyleFeedback(prev => {
          const newSet = new Set(prev);
          newSet.delete(updateKey);
          return newSet;
        });
      }
    }
  }, [updateScore, styleModalData, getScoreDataForRoutineJudge]);

  // Boolean flag update handler
  const handleBooleanFlagUpdate = useCallback(async (routineId: string, judgeId: string, flagType: 'iChoreographedThis' | 'notFamilyFriendly', newValue: boolean) => {
    if (!updateScore) return;

    const updateKey = `${routineId}_${judgeId}_${flagType}`;
    
    // Store the target state
    setBooleanFlagTargets(prev => new Map(prev).set(updateKey, newValue));
    
    // Set loading state
    setUpdatingBooleanFlags(prev => new Set([...prev, updateKey]));

    try {
      // Get existing score data to preserve other fields
      const existingScoreData = getScoreDataForRoutineJudge(routineId, judgeId);
      
      if (!existingScoreData) {
        console.error('No existing score data found for this judge');
        return;
      }
      
      await updateScoreWithOriginalLocation({
        judgeId: judgeId,
        routineId: routineId,
        routineIndex: 0, // Not used for global updates
        scores: existingScoreData.scores || {},
        comments: existingScoreData.comments || '',
        isSubmitted: existingScoreData.isSubmitted || false,
        [flagType]: newValue
      });
      
      // Clear loading state after a brief delay
      setTimeout(() => {
        setUpdatingBooleanFlags(prev => {
          const newSet = new Set(prev);
          newSet.delete(updateKey);
          return newSet;
        });
        setBooleanFlagTargets(prev => {
          const newMap = new Map(prev);
          newMap.delete(updateKey);
          return newMap;
        });
      }, 1500);
      
    } catch (error) {
      console.error('Boolean flag update failed:', error);
      setUpdatingBooleanFlags(prev => {
        const newSet = new Set(prev);
        newSet.delete(updateKey);
        return newSet;
      });
      setBooleanFlagTargets(prev => {
        const newMap = new Map(prev);
        newMap.delete(updateKey);
        return newMap;
      });
    }
  }, [updateScore, globalScoreUpdates]);

  // Reset score handlers
  const handleResetClick = useCallback((routineId: string, judgeId: string) => {
    const resetKey = `${routineId}_${judgeId}`;
    setResetConfirmation(prev => new Set([...prev, resetKey]));
  }, []);

  const handleResetConfirm = useCallback(async (routineId: string, judgeId: string) => {
    if (!deleteScore) return;

    const resetKey = `${routineId}_${judgeId}`;
    
    try {
      await deleteScore(judgeId, routineId);
      
      // Clear confirmation state
      setResetConfirmation(prev => {
        const newSet = new Set(prev);
        newSet.delete(resetKey);
        return newSet;
      });
    } catch (error) {
      console.error('Score deletion failed:', error);
      // Clear confirmation state even on error
      setResetConfirmation(prev => {
        const newSet = new Set(prev);
        newSet.delete(resetKey);
        return newSet;
      });
    }
  }, [deleteScore]);

  const handleResetCancel = useCallback((routineId: string, judgeId: string) => {
    const resetKey = `${routineId}_${judgeId}`;
    setResetConfirmation(prev => {
      const newSet = new Set(prev);
      newSet.delete(resetKey);
      return newSet;
    });
  }, []);

  // Auto-cancel reset confirmation after 5 seconds
  useEffect(() => {
    if (resetConfirmation.size > 0) {
      const timer = setTimeout(() => {
        setResetConfirmation(new Set());
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [resetConfirmation]);

  // Choreographer score replacement handler
  const handleChoreographerScoreReplacement = useCallback(async (routineId: string, judgeId: string) => {
    if (!updateScore) return;

    try {
      // Get all judge scores for this routine
      const routineJudgeScores = getJudgeScores(routineId);
      
      // Filter out the choreographer and get other judges' scores
      const otherJudgeScores = routineJudgeScores.filter(js => 
        js.judgeId !== judgeId && !js.iChoreographedThis
      );
      
      if (otherJudgeScores.length === 0) {
        console.warn('No other judge scores available for replacement');
        return;
      }
      
      // Calculate average scores for each criterion
      const existingScoreData = getScoreDataForRoutineJudge(routineId, judgeId);
      if (!existingScoreData) return;
      
      const replacementScores: { [criterionId: string]: number } = {};
      
      if (scoringMode === 'simple') {
        // For simple scoring, average the simple scores
        const simpleScores = otherJudgeScores
          .map(js => js.totalScore)
          .filter(score => score > 0);
        
        if (simpleScores.length > 0) {
          replacementScores.simpleScore = simpleScores.reduce((sum, score) => sum + score, 0) / simpleScores.length;
        }
      } else {
        // For criteria-based scoring, average each criterion
        criteria.forEach(criterion => {
          const criterionScores = otherJudgeScores
            .map(js => js.scores[criterion.id])
            .filter(score => score !== undefined && score !== null && score > 0);
          
          if (criterionScores.length > 0) {
            replacementScores[criterion.id] = criterionScores.reduce((sum, score) => sum + score, 0) / criterionScores.length;
          }
        });
      }
      
      await updateScoreWithOriginalLocation({
        judgeId: judgeId,
        routineId: routineId,
        routineIndex: 0, // Not used for global updates
        scores: replacementScores,
        comments: existingScoreData.comments || '',
        isSubmitted: existingScoreData.isSubmitted || false,
        iChoreographedThis: true // Keep the choreographer flag
      });
      
    } catch (error) {
      console.error('Choreographer score replacement failed:', error);
    }
  }, [updateScoreWithOriginalLocation, globalScoreUpdates, criteria, scoringMode]);

  const toggleShowScores = () => {
    setShowScores(!showScores);
  };
  
  
  // Handle opening style feedback modal
  const handleOpenStyleFeedbackModal = useCallback((routineId: string, judgeId: string, routine: AwardedRoutine) => {
    setStyleModalData({ routineId, judgeId, routine });
    setIsStyleFeedbackModalOpen(true);
  }, []);

  const clearSearch = () => {
    setSearchTerm('');
  };
  
  // Get style score sheets for modal (placeholder - would need to be implemented based on your system)
  const getStyleScoreSheets = useCallback(() => {
    return styleScoreSheets;
  }, [styleScoreSheets]);

  // Filter routines based on search term
  const filteredRoutines = useMemo(() => {
    if (!searchTerm.trim()) return routines;
    
    const searchLower = searchTerm.toLowerCase().trim();
    
    return routines.filter(routine => {
      // Search in routine name
      if (routine.routineName.toLowerCase().includes(searchLower)) return true;
      
      // Search in studio name
      if (routine.studio.toLowerCase().includes(searchLower)) return true;
      
      // Search in performer name
      if (routine.performer && routine.performer.toLowerCase().includes(searchLower)) return true;
      
      // Search in routine ID/number
      if (routine.routineId.toString().includes(searchLower)) return true;
      
      // Search in age group
      if (routine.ageGroup.toLowerCase().includes(searchLower)) return true;
      
      // Search in division
      if (routine.division.toLowerCase().includes(searchLower)) return true;
      
      // Search in category
      if (routine.category.toLowerCase().includes(searchLower)) return true;
      
      // Search in award name
      if (routine.award.toLowerCase().includes(searchLower)) return true;
      
      // Search in stage info
      if (routine.stageInfo && routine.stageInfo.toLowerCase().includes(searchLower)) return true;
      
      return false;
    });
  }, [routines, searchTerm]);

  useEffect(() => {
    console.log('🎭 AwardsTracker styleScoreSheets:', styleScoreSheets);
  }, [styleScoreSheets]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Inject custom breathing animation styles */}
      <style dangerouslySetInnerHTML={{ __html: breathingStyles }} />
      {/* Header */}
      <div className="flex-shrink-0 mb-4">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-xl font-black text-white">
              GLOBAL AWARDS
            </h3>
            {baseCompetitionCode && (
              <div className="text-xs text-white/60 mt-1">
                All Stages • {baseCompetitionCode}
                {lastUpdated && (
                  <span className="ml-2">
                    • Updated {lastUpdated.toLocaleTimeString()}
                    {connected && (
                      <span className="ml-1 text-green-400">• Live Updates</span>
                    )}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Editing Toggle Button - only show if enableEditing is true */}
            {enableEditing && (
              <button
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-purple-500/30 to-pink-500/30 border border-purple-400/50 text-purple-200 hover:from-purple-500/40 hover:to-pink-500/40 transition-all duration-300 backdrop-blur-xl text-xs font-medium"
                title="Tabulator Editing Mode Active"
              >
                <Settings className="w-3 h-3" />
                <span>Edit Mode</span>
              </button>
            )}
            
            {/* Auto-Update Status / Manual Refresh Button */}
            <button
              onClick={fetchGlobalAwardsData}
              disabled={isLoading}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-all duration-300 backdrop-blur-xl text-xs font-medium disabled:opacity-50 ${
                connected 
                  ? 'bg-gradient-to-r from-green-500/30 to-emerald-500/30 border-green-400/50 text-green-200 hover:from-green-500/40 hover:to-emerald-500/40' 
                  : 'bg-gradient-to-r from-blue-500/30 to-indigo-500/30 border-blue-400/50 text-blue-200 hover:from-blue-500/40 hover:to-indigo-500/40'
              }`}
              title={connected ? "Auto-updating • Click to force refresh" : "Manual refresh (disconnected)"}
            >
              {isLoading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : connected ? (
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span>Live</span>
                </div>
              ) : (
                <RefreshCcw className="w-3 h-3" />
              )}
            </button>
            
            {/* Show/Hide Scores Toggle */}
            <button
              onClick={toggleShowScores}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all duration-300 backdrop-blur-xl text-xs font-medium ${
                showScores
                  ? 'bg-gradient-to-r from-green-500/30 to-emerald-500/30 border border-green-400/50 text-green-200 hover:from-green-500/40 hover:to-emerald-500/40'
                  : 'bg-gradient-to-r from-gray-500/30 to-gray-600/30 border border-gray-400/50 text-gray-200 hover:from-gray-500/40 hover:to-gray-600/40'
              }`}
              title={showScores ? 'Hide Scores' : 'Show Scores'}
            >
              {showScores ? (
                <>
                  <Eye className="w-3 h-3" />
                  <span>Scores</span>
                </>
              ) : (
                <>
                  <EyeOff className="w-3 h-3" />
                  <span>Hidden</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type="text"
              placeholder="Search routines, studios, performers, awards... (Ctrl+F)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-400/50 focus:border-purple-400/50 transition-all duration-300 text-sm"
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40 hover:text-white/70 transition-colors"
                title="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Results Count */}
        <div className="flex justify-between items-center">
          <div className="text-xs text-white/60">
            {searchTerm ? (
              <>
                Showing <span className="font-bold text-white/80">{filteredRoutines.length}</span> of <span className="font-bold text-white/80">{routines.length}</span> awards
                {searchTerm && (
                  <> for "<span className="font-medium text-purple-300">{searchTerm}</span>"</>
                )}
              </>
            ) : (
              <>
                <span className="font-bold text-white/80">{routines.length}</span> {routines.length === 1 ? 'award' : 'awards'} total
              </>
            )}
          </div>
          
          {filteredRoutines.length === 0 && searchTerm && (
            <button
              onClick={clearSearch}
              className="text-xs px-2 py-1 rounded bg-red-500/20 text-red-300 hover:bg-red-500/30 transition-colors"
            >
              Clear search
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div 
        className="routines-container flex-1 min-h-0 overflow-y-auto overflow-x-hidden pr-1"
        style={{
          transform: 'translateZ(0)',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl flex items-center justify-center border border-purple-400/40">
                <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
              </div>
              <div className="text-white/70 text-sm font-medium">Loading Awards</div>
            </div>
          </div>
        ) : filteredRoutines.length === 0 ? (
          <div className="flex justify-center items-center h-full">
            <div className="text-center">
              {searchTerm ? (
                <>
                  <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-gray-500/20 to-slate-500/20 rounded-2xl flex items-center justify-center border border-gray-400/40">
                    <Search className="w-6 h-6 text-gray-400" />
                  </div>
                  <div className="text-white/70 text-sm font-medium mb-2">No Results Found</div>
                  <div className="text-white/50 text-xs mb-4">
                    No awards match "<span className="font-medium text-purple-300">{searchTerm}</span>"
                  </div>
                  <button
                    onClick={clearSearch}
                    className="text-xs px-3 py-1.5 rounded-lg bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 transition-colors border border-purple-400/40"
                  >
                    Clear search
                  </button>
                </>
              ) : routines.length === 0 ? (
                <>
                  <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-2xl flex items-center justify-center border border-yellow-400/40">
                    <Trophy className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div className="text-white/70 text-sm font-medium mb-2">No Awards Yet</div>
                  <div className="text-white/50 text-xs">Awards will appear as routines are scored</div>
                </>
              ) : null}
            </div>
          </div>
        ) : (
          filteredRoutines.map((routine, index) => (
            <motion.div
              key={routine.routineId}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: Math.min(index * 0.02, 0.3) }}
              className={`p-4 rounded-lg border transition-colors ${
                index % 2 === 1 ? 'bg-white/5 border-white/10' : 'bg-transparent border-white/5'
              } ${index === filteredRoutines.length - 1 ? 'mb-4' : 'mb-3'}`}
            >
                {/* Routine Header */}
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-grow min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-white/60">#{routine.routineId}</span>
                      <span className="text-sm font-bold text-white truncate">
                        {routine.routineName}
                      </span>
                    </div>
                    <div className="text-xs text-white/60">
                      {routine.studio} • {routine.ageGroup} {routine.category} {routine.division}
                      {routine.stageInfo && (
                        <> • {routine.stageInfo}</>
                      )}
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <div className={`text-sm font-bold ${
                      showScores ? 'text-red-400' : 'text-white'
                    }`}>
                      {showScores ? (
                        <>
                          {/* Check if any scores for this routine are being updated */}
                          {Array.from(updatingScores).some(key => key.startsWith(`${routine.internalId}_`)) ? (
                            <span className="flex items-center gap-1 justify-end">
                              <div className="flex items-center space-x-1">
                                <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-breathing"></div>
                                <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-breathing" style={{ animationDelay: '0.1s' }}></div>
                                <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-breathing" style={{ animationDelay: '0.2s' }}></div>
                              </div>
                              <span className="text-yellow-400">
                                {routine.award} ({routine.finalScore.toFixed(0)}
                                {routine.flexibleScoreData?.droppedScores && routine.flexibleScoreData.droppedScores.length > 0 && (
                                  <>:{routine.flexibleScoreData.droppedScores.map((d: any) => 
                                    d.totalScore || d.originalScore?.totalScore || 0
                                  ).join(',')}</>
                                )})
                              </span>
                            </span>
                          ) : (
                            <>
                              {routine.award} ({routine.finalScore.toFixed(0)}
                              {routine.flexibleScoreData?.droppedScores && routine.flexibleScoreData.droppedScores.length > 0 && (
                                <>:{routine.flexibleScoreData.droppedScores.map((d: any) => 
                                  d.totalScore || d.originalScore?.totalScore || 0
                                ).join(',')}</>
                              )})
                            </>
                          )}
                        </>
                      ) : (
                        routine.award
                      )}
                    </div>
                  </div>
                </div>

                {/* Enhanced Judge Scores with Editing */}
                {showScores && routine.judgeScores && routine.judgeScores.length > 0 && (
                  <div className="mt-2">
                    <div className="flex flex-wrap gap-1">
                      {routine.judgeScores.map((judgeScore, judgeIndex) => {
                        const judgeProfile = comprehensiveJudgeProfiles[judgeScore.judgeId];
                        const scoreData = getScoreDataForRoutineJudge(routine.internalId, judgeScore.judgeId);
                        const resetKey = `${routine.internalId}_${judgeScore.judgeId}`;
                        const isInResetConfirmation = resetConfirmation.has(resetKey);
                        
                        return (
                          <div
                            key={judgeScore.judgeId}
                            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs relative group ${
                              judgeScore.iChoreographedThis 
                                ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-400/30' 
                                : 'bg-white/10 text-white/80 border border-white/10'
                            } ${enableEditing ? 'hover:bg-white/20 transition-all duration-200' : ''}`}
                            title={`${judgeScore.judgeName || judgeScore.judgeId}${judgeScore.judgePosition !== undefined ? ` (Position ${judgeScore.judgePosition})` : ''}: ${judgeScore.totalScore.toFixed(0)}${judgeScore.iChoreographedThis ? ' (Choreographer)' : ''}${enableEditing ? ' - Click to edit' : ''}`}
                          >
                            {/* Judge Photo or Initials */}
                            {judgeProfile?.photoUrl ? (
                              <img
                                src={judgeProfile.photoUrl}
                                alt={judgeScore.judgeName || judgeScore.judgeId}
                                className="w-5 h-5 rounded-full object-cover border border-white/20"
                                onError={(e) => {
                                  // Fallback to initials if image fails to load
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                  const initialsSpan = target.nextElementSibling as HTMLElement;
                                  if (initialsSpan) initialsSpan.style.display = 'inline';
                                }}
                              />
                            ) : null}
                            <span 
                              className={`font-medium ${judgeProfile?.photoUrl ? 'hidden' : 'inline'}`}
                              style={{ display: judgeProfile?.photoUrl ? 'none' : 'inline' }}
                            >
                              {judgeScore.judgeName ? 
                                judgeScore.judgeName.split(' ').map(name => name.charAt(0)).join('') : 
                                judgeScore.judgeId.substring(0, 2).toUpperCase()
                              }
                            </span>
                            
                            {/* Judge Position Badge */}
                            {judgeScore.judgePosition !== undefined && (
                              <span className="text-xs font-bold bg-blue-500/30 text-blue-200 px-1 rounded">
                                {judgeScore.judgePosition}
                              </span>
                            )}
                            
                            {/* Score Display - inline editing if enabled */}
                            {enableEditing && editingScore?.routineId === routine.internalId && 
                             editingScore?.judgeId === judgeScore.judgeId && 
                             editingScore?.criterionId === 'totalScore' ? (
                              <input
                                type="number"
                                value={tempScoreValue}
                                onChange={(e) => setTempScoreValue(e.target.value)}
                                onBlur={handleScoreBlur}
                                onKeyDown={handleScoreKeyDown}
                                className="w-16 px-1 py-0.5 text-sm bg-gray-800 border border-gray-600 rounded text-white text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                                autoFocus
                                min="0"
                                max={scoringMode === 'simple' ? "100" : (criteria.reduce((sum, c) => sum + (c.maxPoints || 10), 0)).toString()}
                              />
                            ) : (
                              <span 
                                className={`font-bold ${
                                  enableEditing ? 'cursor-pointer hover:text-blue-300 hover:underline' : ''
                                } ${
                                  updatingScores.has(`${routine.internalId}_${judgeScore.judgeId}_totalScore`) 
                                    ? 'animate-score-updating text-yellow-400 px-1 rounded' 
                                    : ''
                                }`}
                                onClick={() => enableEditing && handleScoreClick(routine.internalId, judgeScore.judgeId, 'totalScore')}
                              >
                                {updatingScores.has(`${routine.internalId}_${judgeScore.judgeId}_totalScore`) ? (
                                  <div className="flex items-center space-x-1">
                                    <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-breathing"></div>
                                    <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-breathing" style={{ animationDelay: '0.1s' }}></div>
                                    <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-breathing" style={{ animationDelay: '0.2s' }}></div>
                                  </div>
                                ) : (
                                  judgeScore.totalScore.toFixed(0)
                                )}
                              </span>
                            )}
                            
                            {/* Choreographer Indicator */}
                            {judgeScore.iChoreographedThis && (
                              <span className="text-xs">🎭</span>
                            )}
                            
                            {/* Not Family Friendly Indicator */}
                            {judgeScore.notFamilyFriendly && (
                              <span className="text-xs" title="Not Family Friendly">⚠️</span>
                            )}
                            
                            {/* Editing Controls - Show on hover when editing is enabled */}
                            {enableEditing && (
                              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 border border-gray-700 rounded-lg p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-1 z-10">
                                {/* Edit Comments Button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCommentClick(routine.internalId, judgeScore.judgeId);
                                  }}
                                  className="p-1 rounded hover:bg-purple-500/30 text-purple-400 hover:text-purple-300 transition-colors"
                                  title="Edit comments"
                                >
                                  <MessageCircle size={10} />
                                </button>
                                
                                {/* Edit Style Feedback Button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenStyleFeedbackModal(routine.internalId, judgeScore.judgeId, routine);
                                  }}
                                  className="p-1 rounded hover:bg-pink-500/30 text-pink-400 hover:text-pink-300 transition-colors"
                                  title="Edit style feedback"
                                >
                                  <Sparkles size={10} />
                                </button>
                                
                                {/* Choreographer Toggle */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleBooleanFlagUpdate(routine.internalId, judgeScore.judgeId, 'iChoreographedThis', !judgeScore.iChoreographedThis);
                                  }}
                                  className={`p-1 rounded transition-colors ${
                                    getBooleanFlagDisplayState(routine.internalId, judgeScore.judgeId, 'iChoreographedThis', judgeScore.iChoreographedThis || false)
                                      ? 'bg-yellow-500/30 text-yellow-300 hover:bg-yellow-500/40' 
                                      : 'hover:bg-gray-500/30 text-gray-400 hover:text-gray-300'
                                  } ${updatingBooleanFlags.has(`${routine.internalId}_${judgeScore.judgeId}_iChoreographedThis`) ? 'animate-breathing' : ''}`}
                                  title={judgeScore.iChoreographedThis ? 'Unmark as choreographer' : 'Mark as choreographer'}
                                >
                                  <UserCheck size={10} />
                                </button>
                                
                                {/* Replace with Average button - only show for choreographer judges */}
                                {judgeScore.iChoreographedThis && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleChoreographerScoreReplacement(routine.internalId, judgeScore.judgeId);
                                    }}
                                    className="p-1 rounded bg-blue-500/30 text-blue-300 hover:bg-blue-500/40 transition-colors"
                                    title="Replace with average of other judges"
                                  >
                                    <Calculator size={10} />
                                  </button>
                                )}
                                
                                {/* Not Family Friendly Toggle */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleBooleanFlagUpdate(routine.internalId, judgeScore.judgeId, 'notFamilyFriendly', !judgeScore.notFamilyFriendly);
                                  }}
                                  className={`p-1 rounded transition-colors ${
                                    getBooleanFlagDisplayState(routine.internalId, judgeScore.judgeId, 'notFamilyFriendly', judgeScore.notFamilyFriendly || false)
                                      ? 'bg-orange-500/30 text-orange-300 hover:bg-orange-500/40' 
                                      : 'hover:bg-gray-500/30 text-gray-400 hover:text-gray-300'
                                  } ${updatingBooleanFlags.has(`${routine.internalId}_${judgeScore.judgeId}_notFamilyFriendly`) ? 'animate-breathing' : ''}`}
                                  title={judgeScore.notFamilyFriendly ? 'Unmark as not family friendly' : 'Mark as not family friendly'}
                                >
                                  <ShieldAlert size={10} />
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              </motion.div>
            ))
        )}
      </div>

      {/* Footer */}
      {lastUpdated && (
        <div className="flex-shrink-0 mt-4 pt-4 border-t border-white/10">
          <div className="text-xs text-white/50 text-center">
            Last updated: {lastUpdated.toLocaleTimeString()}
            {enableEditing && (
              <span className="ml-2 text-purple-400">• Editing Mode Active</span>
            )}
          </div>
        </div>
      )}
      
      {/* Style Feedback Editor Modal */}
      {isStyleFeedbackModalOpen && styleModalData && (
        <StyleFeedbackEditorModal
          isOpen={isStyleFeedbackModalOpen}
          onClose={() => {
            setIsStyleFeedbackModalOpen(false);
            setStyleModalData(null);
          }}
          currentRoutine={{
            id: styleModalData.routineId,
            number: styleModalData.routineId,
            name: `Routine #${styleModalData.routineId}`,
            category: styleModalData.routine.category,        // Add category for style sheet matching
            division: styleModalData.routine.division,        // Add division  
            ageGroup: styleModalData.routine.ageGroup,        // Add age group
            studio: styleModalData.routine.studio,            // Add studio
            performer: styleModalData.routine.performer,      // Add performer
            routineName: styleModalData.routine.routineName   // Add routine name
          }}
          judgeIds={styleModalData.routine.judgeScores?.map((js: JudgeScore) => js.judgeId) || [styleModalData.judgeId]}
          judgeProfiles={comprehensiveJudgeProfiles}
          judgeStyleFeedback={
            styleModalData.routine.judgeScores?.reduce((acc: any, js: JudgeScore) => {
              acc[js.judgeId] = js.styleFeedback || {};
              return acc;
            }, {}) || {
              [styleModalData.judgeId]: getScoreDataForRoutineJudge(styleModalData.routineId, styleModalData.judgeId)?.styleFeedback || {}
            }
          }
          judgePositions={
            styleModalData.routine.judgeScores?.reduce((acc: any, js: JudgeScore) => {
              if (js.judgePosition !== undefined) {
                acc[js.judgeId] = js.judgePosition;
              }
              return acc;
            }, {}) || {
              [styleModalData.judgeId]: (() => {
                const scoreData = getScoreDataForRoutineJudge(styleModalData.routineId, styleModalData.judgeId);
                return scoreData?.judgeData?.judgePosition || undefined;
              })()
            }
          }
          styleScoreSheets={getStyleScoreSheets()}
          onFeedbackUpdate={(judgeId, category, criteria, feedbackType, batchedData) => 
            handleStyleFeedbackUpdate(judgeId, category, criteria, feedbackType, batchedData)
          }
          initialJudgeIndex={styleModalData.routine.judgeScores?.findIndex((js: JudgeScore) => js.judgeId === styleModalData.judgeId) || 0}
        />
      )}
      
      {/* Comment Edit Modal - Inline editing handled in judge score display */}
      {editingComment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">
              Edit Comments - Routine #{editingComment.routineId}
            </h3>
            <textarea
              value={tempCommentValue}
              onChange={(e) => setTempCommentValue(e.target.value)}
              onKeyDown={handleCommentKeyDown}
              className="w-full h-32 p-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              placeholder="Enter comments for this judge..."
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => {
                  setEditingComment(null);
                  setTempCommentValue('');
                }}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCommentBlur}
                disabled={updatingComments.has(`${editingComment.routineId}_${editingComment.judgeId}`)}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {updatingComments.has(`${editingComment.routineId}_${editingComment.judgeId}`) ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AwardsTracker;
